import { useState, useEffect, useRef } from 'react'
import './SignalFeed.css'

export default function SignalFeed({ signals, watchlist }) {
  const [filter, setFilter] = useState('all')
  const [paused, setPaused] = useState(false)
  const [expandedId, setExpandedId] = useState(null)
  const [modalSignal, setModalSignal] = useState(null)
  const [newSignalIds, setNewSignalIds] = useState(new Set())
  const [hotTickers, setHotTickers] = useState({})
  const feedRef = useRef(null)
  const prevSignalsRef = useRef([])

  // Track new signals for animation
  useEffect(() => {
    const prevIds = new Set(prevSignalsRef.current.map(s => s.id))
    const newIds = signals.filter(s => !prevIds.has(s.id)).map(s => s.id)

    if (newIds.length > 0) {
      setNewSignalIds(new Set(newIds))
      // Haptic feedback on mobile
      if (navigator.vibrate) navigator.vibrate(5)
      // Clear new status after animation
      setTimeout(() => setNewSignalIds(new Set()), 3000)
    }

    prevSignalsRef.current = signals
  }, [signals])

  // Calculate "hot" tickers (multiple signals in short time)
  useEffect(() => {
    const tickerCounts = {}
    const recentCutoff = Date.now() - 3600000 // Last hour

    signals.forEach(s => {
      if (s.timestamp > recentCutoff) {
        s.tickers.forEach(t => {
          tickerCounts[t] = (tickerCounts[t] || 0) + 1
        })
      }
    })

    // Only mark as hot if 3+ signals
    const hot = {}
    Object.entries(tickerCounts).forEach(([ticker, count]) => {
      if (count >= 3) hot[ticker] = count
    })
    setHotTickers(hot)
  }, [signals])

  // Auto-scroll to top when new signals arrive (unless paused)
  useEffect(() => {
    if (!paused && feedRef.current) {
      feedRef.current.scrollTop = 0
    }
  }, [signals, paused])

  const formatTime = (timestamp) => {
    const diff = Date.now() - timestamp
    const minutes = Math.floor(diff / 60000)
    if (minutes < 1) return 'now'
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    return `${hours}h`
  }

  const getSourceIcon = (source) => {
    const icons = {
      'Reuters': 'R',
      'Bloomberg': 'B',
      'WSJ': 'W',
      'SEC Filing': 'S',
      'TechCrunch': 'T',
      'FCC': 'F',
      'EPA': 'E',
    }
    return icons[source] || source.charAt(0).toUpperCase()
  }

  const filteredSignals = signals.filter(signal => {
    if (filter === 'all') return true
    if (filter === 'watchlist') {
      return signal.tickers.some(t => watchlist.includes(t))
    }
    return true
  })

  const hasWatchlistMatch = (tickers) => {
    return tickers.some(t => watchlist.includes(t))
  }

  return (
    <aside className="panel-right">
      <div className="panel-header">
        <span className="panel-title">Live Feed</span>
        <div className="feed-controls">
          <select
            className="feed-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="watchlist">Watchlist</option>
          </select>
          <button
            className={`feed-pause-btn ${paused ? 'paused' : ''}`}
            onClick={() => setPaused(!paused)}
            title={paused ? 'Resume' : 'Pause'}
          >
            {paused ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className="panel-content" ref={feedRef}>
        {filteredSignals.map((signal, index) => {
          const isNew = newSignalIds.has(signal.id)
          const hasHotTicker = signal.tickers.some(t => hotTickers[t])
          const isWatchlistMatch = hasWatchlistMatch(signal.tickers)

          return (
            <div
              key={signal.id}
              className={`signal-item ${isWatchlistMatch ? 'watchlist-match' : ''} ${isNew ? 'signal-new' : ''} ${hasHotTicker ? 'signal-hot' : ''}`}
              onClick={() => setModalSignal(signal)}
            >
              {/* Hot/New badges */}
              <div className="signal-badges">
                {isNew && <span className="signal-badge new-badge">NEW</span>}
                {hasHotTicker && <span className="signal-badge hot-badge">🔥 HOT</span>}
              </div>

              <div className="signal-header">
                <div className="signal-source">
                  <span className="signal-source-icon">{getSourceIcon(signal.source)}</span>
                  <span className="signal-source-name">{signal.source}</span>
                </div>
                <span className="signal-time">{formatTime(signal.timestamp)}</span>
              </div>

              <p className="signal-headline" style={{
                WebkitLineClamp: expandedId === signal.id ? 'unset' : 2,
                display: expandedId === signal.id ? 'block' : '-webkit-box'
              }}>
                {signal.headline}
              </p>

              <div className="signal-tickers">
                {signal.tickers.map(ticker => (
                  <span
                    key={ticker}
                    className={`signal-ticker ${watchlist.includes(ticker) ? 'on-watchlist' : ''} ${hotTickers[ticker] ? 'ticker-hot' : ''}`}
                  >
                    ${ticker}
                    {hotTickers[ticker] && <span className="ticker-count">{hotTickers[ticker]}</span>}
                  </span>
                ))}
              </div>
            </div>
          )
        })}

        {filteredSignals.length === 0 && (
          <div style={{
            padding: 'var(--space-6)',
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: 'var(--text-sm)'
          }}>
            {filter === 'watchlist'
              ? 'No signals for watchlist tickers yet.'
              : 'Waiting for signals...'}
          </div>
        )}
      </div>

      {/* Feed status footer */}
      <div style={{
        padding: 'var(--space-2) var(--space-4)',
        borderTop: '1px solid var(--border-panel)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--text-muted)'
        }}>
          {filteredSignals.length} signals
        </span>
        {paused && (
          <span style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--accent-yellow)',
            fontFamily: 'var(--font-mono)'
          }}>
            PAUSED
          </span>
        )}
      </div>

      {/* Intel Lightbox Modal */}
      {modalSignal && (
        <div
          className="intel-modal-overlay"
          onClick={() => setModalSignal(null)}
        >
          <div
            className="intel-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="intel-modal-close"
              onClick={() => setModalSignal(null)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div className="intel-modal-header">
              <div className="intel-modal-source">
                <span className="intel-modal-source-icon">{getSourceIcon(modalSignal.source)}</span>
                <span className="intel-modal-source-name">{modalSignal.source}</span>
              </div>
              <span className="intel-modal-time">{formatTime(modalSignal.timestamp)}</span>
            </div>

            <h2 className="intel-modal-headline">{modalSignal.headline}</h2>

            <div className="intel-modal-tickers">
              {modalSignal.tickers.map(ticker => (
                <span
                  key={ticker}
                  className={`intel-modal-ticker ${watchlist.includes(ticker) ? 'on-watchlist' : ''} ${hotTickers[ticker] ? 'ticker-hot' : ''}`}
                >
                  ${ticker}
                  {hotTickers[ticker] && <span className="ticker-count">{hotTickers[ticker]}</span>}
                </span>
              ))}
            </div>

            {modalSignal.summary && (
              <div className="intel-modal-summary">
                <h3>Summary</h3>
                <p>{modalSignal.summary}</p>
              </div>
            )}

            {modalSignal.url && (
              <a
                href={modalSignal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="intel-modal-link"
              >
                Read Full Article
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </a>
            )}
          </div>
        </div>
      )}
    </aside>
  )
}
